import { defineComponent as _defineComponent } from 'vue'
import {ref, onMounted, watch} from "vue";
import VModal from "@/components/vModal.vue";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import {getErrorMessage} from "@/utils/Helpers";
import {useNotify} from "@/hooks/useNotify";
import {useTranslations} from "@/hooks/useTranslations";
import VAddVendorModal from "@/components/costing/vAddVendorModal.vue";
import {Vendor} from "@/models/IngredientCost/Vendor";
import VAdditionalMeasurementModal from "@/components/costing/vAdditionalMeasurementModal.vue";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";
import {ModifyIngredientCostDto} from "@/models/RecipeInvoice/ModifyIngredientCostDto";


export default /*#__PURE__*/_defineComponent({
  __name: 'VAddBulkCostModal',
  props: {
    show: { type: Boolean },
    newIngredientCosts: null
  },
  emits: [
  "on-cancel",
  "on-save"
],
  setup(__props: any, { emit }) {

const props = __props as {
  show: boolean,
  newIngredientCosts: ModifyIngredientCostDto[],
};





const vendors = ref<any[]>([]);
const showAddingVendorModal = ref(false);
const showAddingMeasurementrModal = ref(false);
const notify = useNotify();
const translations = useTranslations();
const editMeasurementAdditionalCost = ref<AdditionalCost>({
  id: 0,
  name: '',
  cost: null,
  restaurantId: 0,
  vendor: { id: 0, name: '' } as Vendor,
  quantity: null,
  ingredientCostMeasurement: {
    measurement: {
      conversionCoeficient: 0,
      isDefault: false,
      measurementCategory: MeasurementCategory.Mass,
      name: "",
      id: 0,
    },
    weightInGrams: 0,
    measurementId: 0,
    ingredientCostId: 0,
    createdOn: null,
    id: 0
  },
  ingredientCostMeasurements: [],
});
const localAdditionalCosts = ref<ModifyIngredientCostDto[]>([]);

onMounted(() => {
});

watch(
  () => props.newIngredientCosts,
  (newVal) => {
    localAdditionalCosts.value = newVal ? [...newVal] : [];
  },
  { immediate: true }
);

function loadVendors() {
  if (!vendors.value.length) {
    IngredientCostRepository.listIngredientCostVendors()
      .then((res) => vendors.value = res.data.result)
      .catch((err) => notify.error(getErrorMessage(err, translations)));
  }
}

function handleVendorAdded(vendorList: Vendor[]) {
  vendors.value = vendorList;
}

function closeModal() {
  localAdditionalCosts.value = [];
  emit("on-cancel");
}

function openAddVendorModal() {
  showAddingVendorModal.value = true;
}

function handleSaveMeasurements(measurements: any[]) {
  const updatedCosts = [...localAdditionalCosts.value];
  const index = updatedCosts.findIndex(
    (x) => x === editMeasurementAdditionalCost.value
  );

  if (index === -1) {
    console.log("No matching additional cost found for measurement update.");
    showAddingMeasurementrModal.value = false;
    return;
  }

  const existingCost = { ...updatedCosts[index] };

  // Get current measurement names
  const existingNames = existingCost.ingredientCostMeasurements.map(
    (m: any) => m.measurement.name
  );

  // Merge new measurements
  const mergedMeasurements = [...existingCost.ingredientCostMeasurements];

  measurements.forEach((m: any) => {
    if (!existingNames.includes(m.name)) {
      mergedMeasurements.push({
        measurement: {
          id: 0,
          name: m.name,
          conversionCoeficient: 0,
          isDefault: false,
          measurementCategory: MeasurementCategory.Mass,
        },
        weightInGrams: m.weight,
        measurementId: 0,
        ingredientCostId: 0,
        createdOn: null,
        id: 0,
      });
    }
  });

  // Update the target cost
  existingCost.ingredientCostMeasurements = mergedMeasurements;
  updatedCosts[index] = existingCost;
  localAdditionalCosts.value[index] = existingCost;

  showAddingMeasurementrModal.value = false;
}

function addNewIngrCostRow() {
  localAdditionalCosts.value.push({
    name: "",
    vendorId: vendors.value[0].id || null,
    quantity: 0,
    chosenMeasurementName: "",
    ingredientCostMeasurements: [],
    cost: 0,
  });
}

function removeIngrCostRow(index: number) {
  localAdditionalCosts.value.splice(index, 1);
}

function openAddMeasurementModal(index: number, ingrCost: any) {
  console.log(ingrCost);
  editMeasurementAdditionalCost.value = ingrCost;
  showAddingMeasurementrModal.value = true;
}

async function saveIngredientCost() {
  const names = localAdditionalCosts.value.map((x) => x.name.trim().toLowerCase());

  const hasDuplicate = names.some(
    (name, index) => names.indexOf(name) !== index && name !== ""
  );

  if (hasDuplicate) {
    notify.error(translations.value.get("additional_cost_error_name_exist"));
    return;
  }

  const hasInvalidMeasurement = localAdditionalCosts.value.some((x) => {
    if (!x || !x.chosenMeasurementName) return true;

    const chosen = x.chosenMeasurementName.trim();
    if (!chosen) return true;

    const validNames = (x.ingredientCostMeasurements || [])
      .map((m) => m.measurement.name.trim().toLowerCase())
      .filter(Boolean);

    return !validNames.includes(chosen.toLowerCase());
  });

  if (hasInvalidMeasurement) {
    notify.error(translations.value.get("additional_cost_error_measurement_missing"));
    return;
  }

  const payload = localAdditionalCosts.value.map((cost) => ({
    name: cost.name,
    vendorId: cost.vendorId || null,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName: cost.chosenMeasurementName || "g",
    ingredientCostMeasurements: (cost.ingredientCostMeasurements || []).map((m) => ({
      id: m.id || 0,
      name: m.measurement.name || "",
      weightInGrams: m.weightInGrams,
    })),
  }));

  try{
    await AdditionalCostRepository.createAdditionalCost(payload);

    emit("on-save", localAdditionalCosts.value);
    notify.success(translations.value.get("ms_save_success"), { closeOnClick: true, timeout: 3500 });
    closeModal();
  }
  catch(error){
    notify.error("Please fix the errors.", { closeOnClick: true, timeout: 3500 });
    return;
  }
}


return { __sfc: true,props, emit, vendors, showAddingVendorModal, showAddingMeasurementrModal, notify, translations, editMeasurementAdditionalCost, localAdditionalCosts, loadVendors, handleVendorAdded, closeModal, openAddVendorModal, handleSaveMeasurements, addNewIngrCostRow, removeIngrCostRow, openAddMeasurementModal, saveIngredientCost, VModal, VAddVendorModal, VAdditionalMeasurementModal }
}

})